import type { Locale } from "date-fns";
// Imported per locale rather than from the "date-fns/locale" barrel: the barrel
// re-exports ~200 locales as ESM-only, which Jest cannot parse and which risks
// pulling every locale into the bundle. Each subpath also ships a CJS build.
import { enUS } from "date-fns/locale/en-US";
import { es } from "date-fns/locale/es";
import { fr } from "date-fns/locale/fr";
import { ko } from "date-fns/locale/ko";
import { ptBR } from "date-fns/locale/pt-BR";
import { ru } from "date-fns/locale/ru";
import { zhCN } from "date-fns/locale/zh-CN";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { DEFAULT_LANGUAGE_CODE } from "./languageConfig";

/**
 * react-datepicker draws its own calendar chrome — the "August 2026" caption, the
 * month/year dropdowns and the Su/Mo/Tu weekday headers — from date-fns locale data.
 * i18next never sees those strings, so translating the surrounding modal leaves the
 * calendar in English.
 *
 * There are 70+ DatePicker call sites in this app, so instead of threading a `locale`
 * prop through every one we register the locales once and swap react-datepicker's
 * default whenever the language changes. Every picker then follows the header
 * language switcher for free, including modules not yet translated.
 */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  es: es,
  zh: zhCN,
  ru: ru,
  ko: ko,
  "pt-BR": ptBR,
  fr: fr,
};

Object.entries(DATE_FNS_LOCALES).forEach(([code, locale]) => {
  registerLocale(code, locale);
});

/**
 * Maps an i18next language onto a registered date-picker locale. Handles the
 * region-tagged codes the browser detector can produce ("en-GB" -> "en") and the
 * reverse case where only a regional variant is supported ("pt" -> "pt-BR").
 */
export const resolveDatePickerLocale = (language?: string | null): string => {
  if (!language) return DEFAULT_LANGUAGE_CODE;
  if (DATE_FNS_LOCALES[language]) return language;

  const base = language.split("-")[0];
  if (DATE_FNS_LOCALES[base]) return base;

  const regionalMatch = Object.keys(DATE_FNS_LOCALES).find(
    (code) => code.split("-")[0] === base
  );
  return regionalMatch || DEFAULT_LANGUAGE_CODE;
};

export const syncDatePickerLocale = (language?: string | null): void => {
  setDefaultLocale(resolveDatePickerLocale(language));
};

export default DATE_FNS_LOCALES;
